import { SearchResult } from '@statsbomb/parachute-types';
import { COMPARISON_ID, COMPARISON_TYPE } from '@/consts/searchKeys';

// Sub-views available per entity type, mirroring the routes in playerRoutes.tsx / teamRoutes.tsx
// (and usePlayerNavItems / useTeamNavItems). Update here if a new player or team tab is added.
const SUB_VIEWS_BY_TYPE: Record<SearchResult['type'], string[]> = {
  player: ['dashboard', 'radar', 'vis', 'matches'],
  team: ['dashboard', 'radar', 'vis', 'squad', 'matches'],
};

const DEFAULT_SUB_VIEW = 'dashboard';

// Comparison params that reference the previous entity and must not carry across to the new one.
const COMPARISON_PARAMS = [COMPARISON_ID, COMPARISON_TYPE];

interface SearchResultPathArgs {
  pathname: string;
  search: string;
  language: string;
  type: SearchResult['type'];
  id: number;
}

// Drops comparison params and returns a leading-"?" query string, or "" when nothing remains.
const preservedQuery = (search: string): string => {
  const params = new URLSearchParams(search);
  COMPARISON_PARAMS.forEach(param => params.delete(param));
  const query = params.toString();

  return query ? `?${query}` : '';
};

/**
 * Builds the in-app path for a selected search result. It keeps the user on the sub-view they are
 * currently viewing (e.g. pitch vis, radar) when the target entity type supports it, and falls back
 * to the dashboard otherwise.
 *
 * Display/view params (metricKey, pitch-display preset, radar template) are carried over only within
 * the same entity type — there the layout does not remount, so the destination needs those params to
 * render the same view, and they stay valid for the same type. Crossing entity types remounts the
 * layout (which re-initialises the view), so the path is left param-free there. The comparison params
 * are always dropped so a comparison never carries over to the new entity.
 */
export const buildSearchResultPath = ({ pathname, search, language, type, id }: SearchResultPathArgs): string => {
  // Path segments are ['', <lang>, <type>, <id>, <subView>, ...]; child routes beyond the sub-view are ignored.
  const segments = pathname.split('/');
  const currentType = segments[2] ?? '';
  const currentSubView = segments[4] ?? '';
  const isOnEntityPage = currentType === 'player' || currentType === 'team';
  const subView =
    isOnEntityPage && SUB_VIEWS_BY_TYPE[type].includes(currentSubView) ? currentSubView : DEFAULT_SUB_VIEW;

  const query = currentType === type ? preservedQuery(search) : '';

  return `/${language}/${type}/${id}/${subView}${query}`;
};
